/**
 * Minimal promise wrapper over a single IndexedDB object store with out-of-line string keys.
 *
 * Storage is best-effort: IndexedDB unavailable (private mode, disabled), a blocked upgrade, a
 * closed connection or an exceeded quota are logged with `console.warn` and resolve to the
 * fallback (`undefined` / `false`) instead of rejecting, so callers never need a `catch`.
 */
export class IndexedDbStore<T> {
    private _db: Promise<IDBDatabase | null> | null = null;

    constructor(
        private readonly _dbName: string,
        private readonly _storeName: string,
        private readonly _version: number = 1
    ) {}

    async get(key: string): Promise<T | undefined> {
        const { ok, result } = await this._run<T>("readonly", (store) => store.get(key));
        return ok ? result : undefined;
    }

    async put(key: string, value: T): Promise<boolean> {
        return (await this._run("readwrite", (store) => store.put(value, key))).ok;
    }

    async delete(key: string): Promise<boolean> {
        return (await this._run("readwrite", (store) => store.delete(key))).ok;
    }

    async clear(): Promise<boolean> {
        return (await this._run("readwrite", (store) => store.clear())).ok;
    }

    private _open(): Promise<IDBDatabase | null> {
        if (!this._db) {
            this._db = new Promise((resolve) => {
                if (typeof indexedDB === "undefined") {
                    resolve(null);
                    return;
                }

                let request: IDBOpenDBRequest;
                try {
                    request = indexedDB.open(this._dbName, this._version);
                } catch (error) {
                    console.warn(`[IndexedDbStore] cannot open "${this._dbName}"`, error);
                    resolve(null);
                    return;
                }

                request.onupgradeneeded = () => {
                    if (!request.result.objectStoreNames.contains(this._storeName)) {
                        request.result.createObjectStore(this._storeName);
                    }
                };
                request.onsuccess = () => {
                    const db = request.result;
                    // Another tab (or a test) upgrading or deleting the database: let it through
                    // and reopen lazily on the next call.
                    db.onversionchange = () => {
                        db.close();
                        this._db = null;
                    };
                    resolve(db);
                };
                request.onerror = () => {
                    console.warn(`[IndexedDbStore] cannot open "${this._dbName}"`, request.error);
                    this._db = null;
                    resolve(null);
                };
            });
        }
        return this._db;
    }

    private async _run<R>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest): Promise<{ ok: boolean; result?: R }> {
        const db = await this._open();
        if (!db) {
            return { ok: false };
        }

        return new Promise((resolve) => {
            try {
                const transaction = db.transaction(this._storeName, mode);
                const request = action(transaction.objectStore(this._storeName));
                transaction.oncomplete = () => resolve({ ok: true, result: request.result });
                // A failed request (quota exceeded included) aborts the transaction, so this covers both.
                transaction.onabort = () => {
                    console.warn(`[IndexedDbStore] ${mode} on "${this._storeName}" aborted`, transaction.error ?? request.error);
                    resolve({ ok: false });
                };
            } catch (error) {
                // Closed connection or a value that cannot be cloned: drop the handle and reopen next time.
                console.warn(`[IndexedDbStore] ${mode} on "${this._storeName}" failed`, error);
                this._db = null;
                resolve({ ok: false });
            }
        });
    }
}
